// frontend/src/pages/ProfilePage.js
import React, { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../api'
import { clearAuth } from '../auth'
import Navbar from '../components/Navbar'

const font = "'Noto Serif JP', Georgia, serif"
const ink = '#1A0A00'
const muted = '#8A7A5A'
const accent = '#C41E3A'
const gold = '#C9973A'
const cardBorder = '#D4C8A8'
const bg = '#F5F0E8'

const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December']
const BASHO_MONTHS = { '01': 'January', '03': 'March', '05': 'May', '07': 'July', '09': 'September', '11': 'November' }

function fmtBasho(id) {
  if (!id) return ''
  const mm = id.slice(4, 6)
  return `${BASHO_MONTHS[mm] || mm} ${id.slice(0, 4)}`
}

function fmtMemberSince(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return `${FULL_MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

function ordinal(n) {
  if (n == null) return '—'
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// ── FitText — shrinks font until content fits on one line ─────────────────────
function FitText({ text, maxPx = 22, minPx = 11 }) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const fit = () => {
      const parent = el.parentElement
      if (!parent) return
      el.style.fontSize = maxPx + 'px'
      let size = maxPx
      while (size > minPx && el.scrollWidth > parent.clientWidth) {
        size -= 0.5
        el.style.fontSize = size + 'px'
      }
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(el.parentElement)
    return () => ro.disconnect()
  }, [text, maxPx, minPx])

  return (
    <span ref={ref} style={{ display: 'block', whiteSpace: 'nowrap', fontWeight: 900, color: ink, fontFamily: font, lineHeight: 1.1, fontSize: maxPx }}>
      {text}
    </span>
  )
}

// ── Tooltip ────────────────────────────────────────────────────────────────────
function Tooltip({ text }) {
  const [visible, setVisible] = useState(false)
  return (
    <span
      style={s.tooltipWrap}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onClick={() => setVisible(v => !v)}
    >
      <span style={s.tooltipIcon}>?</span>
      {visible && <span style={s.tooltipBox}>{text}</span>}
    </span>
  )
}

// ── Honor card — value + optional inline context list ────────────────────────
function HonorCard({ title, value, tooltip, items }) {
  const MAX = 5
  const shown = (items || []).slice(0, MAX)
  const overflow = (items || []).length - shown.length
  return (
    <div style={s.statCard}>
      <div style={s.statCardTop}>
        <div style={s.statCardTitleRow}>
          <span style={s.statCardTitle}>{title}</span>
          {tooltip && <Tooltip text={tooltip} />}
        </div>
        <FitText text={String(value ?? '—')} />
        {shown.length > 0 && (
          <div style={s.statCardSub}>
            {shown.map((item, i) => <div key={i}>{item}</div>)}
            {overflow > 0 && <div style={{ fontStyle: 'italic' }}>+{overflow} more</div>}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Plain stat card ───────────────────────────────────────────────────────────
function StatCard({ title, value, subLabel, tooltip }) {
  return (
    <div style={s.statCard}>
      <div style={s.statCardTop}>
        <div style={s.statCardTitleRow}>
          <span style={s.statCardTitle}>{title}</span>
          {tooltip && <Tooltip text={tooltip} />}
        </div>
        <FitText text={String(value ?? '—')} />
        {subLabel && <div style={s.statCardSub}>{subLabel}</div>}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { username: paramUsername } = useParams()
  const navigate = useNavigate()
  const currentUser = localStorage.getItem('username')
  const targetUsername = paramUsername || currentUser
  const isOwnProfile = !paramUsername || paramUsername.toLowerCase() === (currentUser || '').toLowerCase()

  // Profile stats (for everyone)
  const [profileStats, setProfileStats] = useState(null)
  const [statsLoading, setStatsLoading] = useState(true)
  const [statsError, setStatsError] = useState(null)

  // Account info (own profile only)
  const [accountInfo, setAccountInfo] = useState(null)
  const [form, setForm] = useState({ username: '', email: '', currentPassword: '', newPassword: '', confirmPassword: '', favoriteRikishi: '' })
  const [savingAccount, setSavingAccount] = useState(false)
  const [savingPrefs, setSavingPrefs] = useState(false)
  const [accountMsg, setAccountMsg] = useState(null)
  const [prefsMsg, setPrefsMsg] = useState(null)
  const [contactForm, setContactForm] = useState({ subject: '', message: '' })
  const [contactSending, setContactSending] = useState(false)
  const [contactMsg, setContactMsg] = useState(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteInput, setDeleteInput] = useState('')
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (!targetUsername) return
    setStatsLoading(true)
    api.get(`/profile/${targetUsername}`)
      .then(res => setProfileStats(res.data))
      .catch(err => {
        if (err.response?.status === 404) setStatsError('User not found.')
        else setStatsError('Failed to load profile.')
      })
      .finally(() => setStatsLoading(false))

    if (isOwnProfile) {
      api.get('/user/me')
        .then(res => {
          setAccountInfo(res.data)
          setForm(f => ({ ...f, username: res.data.username || '', email: res.data.email || '', favoriteRikishi: res.data.favoriteRikishi || '' }))
          // Keep localStorage group info fresh
          if (res.data.groupId != null) {
            localStorage.setItem('groupId', String(res.data.groupId))
            localStorage.setItem('groupName', res.data.groupName || '')
            localStorage.setItem('isDefaultGroup', res.data.isDefaultGroup !== false ? 'true' : 'false')
          }
        })
        .catch(() => {})
    }
  }, [targetUsername, isOwnProfile])

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }))

  const saveAccount = async () => {
    if (form.newPassword && form.newPassword !== form.confirmPassword) {
      setAccountMsg({ type: 'error', text: 'New passwords do not match' }); return
    }
    setSavingAccount(true); setAccountMsg(null)
    try {
      const payload = { email: form.email, username: form.username }
      if (form.newPassword) { payload.currentPassword = form.currentPassword; payload.newPassword = form.newPassword }
      const res = await api.put('/user/me', payload)
      setAccountInfo(p => ({ ...p, ...res.data }))
      setForm(f => ({ ...f, username: res.data.username, currentPassword: '', newPassword: '', confirmPassword: '' }))
      if (res.data.token) {
        const storage = localStorage.getItem('token') ? localStorage : sessionStorage
        storage.setItem('token', res.data.token)
        localStorage.setItem('username', res.data.username)
      }
      setAccountMsg({ type: 'success', text: 'Saved' })
    } catch (err) {
      setAccountMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save' })
    } finally { setSavingAccount(false) }
  }

  const handleToggleReminders = async () => {
    const current = accountInfo.emailReminders !== false
    const next = !current
    setAccountInfo(p => ({ ...p, emailReminders: next }))
    try { await api.put('/user/me', { emailReminders: next }) }
    catch { setAccountInfo(p => ({ ...p, emailReminders: current })) }
  }

  const sendContact = async () => {
    if (!contactForm.subject.trim() || !contactForm.message.trim()) {
      setContactMsg({ type: 'error', text: 'Subject and message are required' }); return
    }
    setContactSending(true); setContactMsg(null)
    try {
      await api.post('/contact', { subject: contactForm.subject.trim(), message: contactForm.message.trim() })
      setContactForm({ subject: '', message: '' })
      setContactMsg({ type: 'success', text: 'Message sent!' })
    } catch {
      setContactMsg({ type: 'error', text: 'Failed to send — try again' })
    } finally { setContactSending(false) }
  }

  const savePrefs = async () => {
    setSavingPrefs(true); setPrefsMsg(null)
    try {
      const res = await api.put('/user/me', { favoriteRikishi: form.favoriteRikishi })
      setAccountInfo(p => ({ ...p, ...res.data }))
      setPrefsMsg({ type: 'success', text: 'Saved' })
    } catch (err) {
      setPrefsMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save' })
    } finally { setSavingPrefs(false) }
  }

  // ── Loading / error states ─────────────────────────────────────────────────
  if (statsLoading) return <div style={s.loading}>Loading…</div>
  if (statsError) return <div style={s.loading}>{statsError}</div>

  const p = profileStats
  const empty = p?.empty || p?.bashoCount === 0

  // ── Derived values for display ─────────────────────────────────────────────
  const honors = p?.honors || {}
  const skill = p?.skill || {}
  const style_ = p?.style || {}

  const handleDeleteAccount = async () => {
    if (deleteInput !== 'DELETE') return
    setDeleting(true)
    try {
      await api.delete('/auth/account')
      clearAuth()
      navigate('/')
    } catch {
      setDeleting(false)
    }
  }

  // Honor card expansion content
  const honorExpansions = {
    yusho: (honors.yusho?.bashos || []).map(b => fmtBasho(b)),
    bestFinish: (honors.bestFinish?.bashos || []).map(b => `${fmtBasho(b)} — ${ordinal(honors.bestFinish.position)}`),
    podiums: (honors.podiums?.bashos || []).map(({ basho, finish }) => `${fmtBasho(basho)} — ${ordinal(finish)}`),
    avgFinish: (honors.avgFinish?.bashos || []).map(({ basho, finish }) => `${fmtBasho(basho)} — ${ordinal(finish)}`),
  }

  return (
    <div style={s.page}>
      <style>{`
        @media (max-width: 768px) {
          .profile-container { padding: 16px 12px 40px !important; }
          .profile-stats-grid { grid-template-columns: 1fr 1fr !important; }
          .profile-history-wrap { overflow-x: auto !important; }
          .profile-history-inner { min-width: 420px; }
        }
        @media (max-width: 420px) {
          .profile-stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <Navbar currentPage="profile" />
      <div className="profile-container" style={s.container}>

        {/* ── Header ────────────────────────────────────────────────────────── */}
        <div style={s.header}>
          <div style={s.redLine} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={s.headerName}>{p?.username}</h1>
            {p?.isBot && <span style={s.botBadge}>🤖</span>}
          </div>
          {p?.isBot && (
            <p style={{ fontSize: 12, color: '#8A7A5A', fontFamily: font, margin: '4px 0 0', fontStyle: 'italic' }}>
              {p.username === 'Robotofuji'
                ? 'Automated ML bot (logistic regression) — competes on the leaderboard but cannot log in'
                : 'Automated bot — picks by rank, competes on the leaderboard but cannot log in'}
            </p>
          )}
          {!p?.isBot && (
            <p style={s.headerSub}>
              {p?.memberSince ? `Member since ${fmtMemberSince(p.memberSince)}` : ''}
              {p?.memberSince && p?.bashoCount ? ' · ' : ''}
              {p?.bashoCount ? `${p.bashoCount} basho played` : ''}
            </p>
          )}
          {!p?.isBot && p?.favoriteRikishi && (
            <p style={s.headerSub}>Favorite rikishi: <span style={{ color: ink, fontWeight: 600 }}>{p.favoriteRikishi}</span></p>
          )}
          {p?.isBot && p?.bashoCount > 0 && (
            <p style={s.headerSub}>{p.bashoCount} basho played</p>
          )}
        </div>

        {/* ── Empty state ───────────────────────────────────────────────────── */}
        {empty ? (
          <div style={s.card}>
            <p style={s.emptyMsg}>No basho played yet — stats will appear after the first basho.</p>
          </div>
        ) : (
          <>
            {/* ── Section 1: Career Honors ──────────────────────────────────── */}
            <div style={s.section}>
              <div style={s.sectionHead}><div style={s.redLine} /><h2 style={s.sectionTitle}>Career Honors</h2></div>
              <div className="profile-stats-grid" style={s.statsGrid}>
                <HonorCard
                  title="Yusho" value={honors.yusho?.count ?? '—'}
                  tooltip="Yusho means tournament championship — won by finishing 1st in a basho."
                  items={honorExpansions.yusho.length ? honorExpansions.yusho : undefined}
                />
                <HonorCard
                  title="Best Finish" value={ordinal(honors.bestFinish?.position)}
                  items={honorExpansions.bestFinish.length ? honorExpansions.bestFinish : undefined}
                />
                <HonorCard
                  title="Podiums" value={honors.podiums?.count ?? '—'}
                  tooltip="Number of basho finished in top 3."
                  items={honorExpansions.podiums.length ? honorExpansions.podiums : undefined}
                />
                <HonorCard
                  title="Avg Finish" value={honors.avgFinish?.value ?? '—'}
                />
              </div>
            </div>

            {/* ── Section 2: Skill ─────────────────────────────────────────── */}
            <div style={s.section}>
              <div style={s.sectionHead}><div style={s.redLine} /><h2 style={s.sectionTitle}>Skill</h2></div>
              <div className="profile-stats-grid" style={s.statsGrid}>
                <StatCard
                  title="All-time Record"
                  value={p.record ? `${p.record.wins}–${p.record.losses}` : '—'}
                  subLabel={p.record?.winPct != null ? `${p.record.winPct}% win rate` : null}
                />
                <StatCard
                  title="Best Basho"
                  value={skill.bestBasho ? `${skill.bestBasho.winPct}%` : '—'}
                  subLabel={skill.bestBasho ? fmtBasho(skill.bestBasho.basho) : null}
                />
                <StatCard
                  title="Best Day"
                  value={skill.bestDay ? skill.bestDay.correct : '—'}
                  subLabel={skill.bestDay ? `Day ${skill.bestDay.day} of ${fmtBasho(skill.bestDay.basho)}` : null}
                />
                <StatCard
                  title="Longest Streak"
                  value={skill.longestStreak?.count ?? '—'}
                  subLabel="Correct picks in a row"
                  tooltip="Most consecutive correct picks across any combination of days and basho."
                />
              </div>
            </div>

            {/* ── Section 3: Style ─────────────────────────────────────────── */}
            <div style={s.section}>
              <div style={s.sectionHead}><div style={s.redLine} /><h2 style={s.sectionTitle}>Style</h2></div>
              <div className="profile-stats-grid" style={s.statsGrid}>
                <StatCard
                  title="Favorite Pick"
                  value={style_.favoritePick?.wrestler ?? '—'}
                  subLabel={style_.favoritePick ? `${style_.favoritePick.pickCount} picks · ${style_.favoritePick.wins} wins` : null}
                  tooltip="The wrestler you've picked most frequently."
                />
                <StatCard
                  title="Lucky Rikishi"
                  value={style_.luckyRikishi?.wrestler ?? '—'}
                  subLabel={style_.luckyRikishi ? `${style_.luckyRikishi.wins} wins when picked` : null}
                  tooltip="The wrestler who has won the most for you."
                />
                <StatCard
                  title="Bogey Rikishi"
                  value={style_.bogeyRikishi?.wrestler ?? '—'}
                  subLabel={style_.bogeyRikishi ? `${style_.bogeyRikishi.losses} losses when picked` : null}
                  tooltip="The wrestler who has lost the most times when you picked them."
                />
                <StatCard
                  title="Contrarian Score"
                  value={style_.contrarian ? `${style_.contrarian.pct}%` : '—'}
                  subLabel="Picks against consensus"
                  tooltip="How often you pick differently from the majority."
                />
              </div>
            </div>

            {/* ── Section 4: Basho History ──────────────────────────────────── */}
            <div style={s.section}>
              <div style={s.sectionHead}><div style={s.redLine} /><h2 style={s.sectionTitle}>Basho History</h2></div>
              <div className="profile-history-wrap" style={s.historyWrap}>
                <div className="profile-history-inner">
                  <div style={s.historyTableCard}>
                    {/* Header */}
                    <div style={s.historyHeaderRow}>
                      <div style={{ ...s.historyCell, flex: 1 }}>Basho</div>
                      <div style={{ ...s.historyCell, ...s.historyNum }}>W</div>
                      <div style={{ ...s.historyCell, ...s.historyNum }}>L</div>
                      <div style={{ ...s.historyCell, ...s.historyNum }}>%</div>
                      <div style={{ ...s.historyCell, ...s.historyNum, width: 64 }}>Finish</div>
                    </div>
                    <div style={s.historyHeaderDivider} />
                    {(p.bashoHistory || []).map(row => {
                      const isPodium = row.finish != null && row.finish <= 3
                      const finishColor = isPodium ? gold : ink
                      return (
                        <div key={row.basho} style={s.historyDataRow}>
                          <div style={{ ...s.historyCell, flex: 1, fontWeight: 500 }}>{fmtBasho(row.basho)}</div>
                          <div style={{ ...s.historyCell, ...s.historyNum }}>{row.wins}</div>
                          <div style={{ ...s.historyCell, ...s.historyNum }}>{row.losses}</div>
                          <div style={{ ...s.historyCell, ...s.historyNum }}>{row.winPct != null ? `${row.winPct}%` : '—'}</div>
                          <div style={{ ...s.historyCell, ...s.historyNum, width: 64, color: finishColor, fontWeight: isPodium ? 700 : 400 }}>
                            {row.isYusho ? '👑 ' : ''}{row.finish != null ? ordinal(row.finish) : '—'}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Account Settings (own profile only, never shown for bots) ──────── */}
        {isOwnProfile && accountInfo && !p?.isBot && (
          <div style={s.card}>
            <div style={s.sectionHead}><div style={s.redLine} /><h2 style={s.sectionTitle}>Account Settings</h2></div>
            <div style={s.field}><label style={s.label}>Username</label>
              <input style={s.input} type="text" value={form.username} onChange={set('username')} /></div>
            <div style={s.field}><label style={s.label}>Email</label>
              <input style={s.input} type="email" value={form.email} onChange={set('email')} /></div>
            <div style={s.divider} />
            <p style={s.sectionLabel}>Change Password</p>
            <div style={s.field}><label style={s.label}>Current password</label>
              <input style={s.input} type="password" value={form.currentPassword} onChange={set('currentPassword')} placeholder="••••••••" autoComplete="current-password" /></div>
            <div style={s.field}><label style={s.label}>New password</label>
              <input style={s.input} type="password" value={form.newPassword} onChange={set('newPassword')} placeholder="••••••••" autoComplete="new-password" /></div>
            <div style={s.field}><label style={s.label}>Confirm new password</label>
              <input style={s.input} type="password" value={form.confirmPassword} onChange={set('confirmPassword')} placeholder="••••••••" autoComplete="new-password" /></div>
            {accountMsg && <p style={s.msg(accountMsg.type)}>{accountMsg.text}</p>}
            <button onClick={saveAccount} disabled={savingAccount} style={s.saveBtn}>
              {savingAccount ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        )}

        {/* ── Preferences + Danger Zone (own profile only, never shown for bots) ── */}
        {isOwnProfile && accountInfo && !p?.isBot && (
          <>
            <div style={s.card}>
              <div style={s.sectionHead}><div style={s.redLine} /><h2 style={s.sectionTitle}>Preferences</h2></div>
              <div style={s.field}><label style={s.label}>Favorite rikishi</label>
                <input style={s.input} type="text" value={form.favoriteRikishi} onChange={set('favoriteRikishi')} placeholder="e.g. Terunofuji" />
              </div>
              {prefsMsg && <p style={s.msg(prefsMsg.type)}>{prefsMsg.text}</p>}
              <button onClick={savePrefs} disabled={savingPrefs} style={s.saveBtn}>
                {savingPrefs ? 'Saving…' : 'Save Preferences'}
              </button>
            </div>

            <div style={s.card}>
              <div style={s.sectionHead}><div style={s.redLine} /><h2 style={s.sectionTitle}>Email Preferences</h2></div>
              <div style={s.toggleRow}>
                <div style={s.toggleInfo}>
                  <div style={s.toggleLabel}>Picks reminder</div>
                  <div style={s.toggleDesc}>Sent when each day's matchups are announced</div>
                </div>
                <button type="button" onClick={handleToggleReminders}
                  style={s.toggleSwitch(accountInfo.emailReminders !== false)}
                  aria-label={accountInfo.emailReminders !== false ? 'Disable picks reminder' : 'Enable picks reminder'}
                >
                  <div style={s.toggleThumb(accountInfo.emailReminders !== false)} />
                </button>
              </div>

              <div style={s.divider} />
              <p style={s.sectionLabel}>Contact Admin</p>
              <div style={s.field}>
                <label style={s.label}>Subject</label>
                <input style={s.input} type="text" value={contactForm.subject} onChange={e => setContactForm(f => ({ ...f, subject: e.target.value }))} placeholder="e.g. Question about scoring" maxLength={120} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Message</label>
                <textarea style={{ ...s.input, height: 100, resize: 'vertical', lineHeight: 1.6 }} value={contactForm.message} onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))} placeholder="What's on your mind?" maxLength={2000} />
              </div>
              {contactMsg && <p style={s.msg(contactMsg.type)}>{contactMsg.text}</p>}
              <button onClick={sendContact} disabled={contactSending} style={s.saveBtn}>
                {contactSending ? 'Sending…' : 'Send Message'}
              </button>
            </div>
            <div style={{ ...s.card, borderColor: deleteOpen ? '#C41E3A' : '#D4C8A8' }}>
              <div style={s.sectionHead}><div style={s.redLine} /><h2 style={s.sectionTitle}>Danger Zone</h2></div>
              {!deleteOpen ? (
                <button onClick={() => setDeleteOpen(true)} style={s.deleteBtn}>
                  Delete Account
                </button>
              ) : (
                <>
                  <p style={{ fontFamily: font, fontSize: 13, color: ink, lineHeight: 1.7, margin: '0 0 16px' }}>
                    This will permanently delete your account and all your picks. This cannot be undone.
                  </p>
                  <p style={{ fontFamily: font, fontSize: 12, color: muted, margin: '0 0 8px' }}>
                    Type <strong>DELETE</strong> to confirm
                  </p>
                  <input
                    style={{ ...s.input, marginBottom: 12 }}
                    value={deleteInput}
                    onChange={e => setDeleteInput(e.target.value)}
                    placeholder="DELETE"
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={handleDeleteAccount}
                      disabled={deleteInput !== 'DELETE' || deleting}
                      style={{ ...s.saveBtn, background: deleteInput === 'DELETE' ? '#C41E3A' : '#B8A88A', flex: 1 }}
                    >
                      {deleting ? 'Deleting…' : 'Permanently Delete Account'}
                    </button>
                    <button onClick={() => { setDeleteOpen(false); setDeleteInput('') }} style={{ ...s.saveBtn, background: '#8A7A5A' }}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh',
    background: bg,
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(180,160,120,0.06) 3px, rgba(180,160,120,0.06) 4px)',
    color: ink,
  },
  loading: { padding: 80, textAlign: 'center', color: muted, fontFamily: font },
  container: { maxWidth: 760, margin: '0 auto', padding: '28px 20px 60px', display: 'flex', flexDirection: 'column', gap: 24 },

  // Header
  header: { paddingBottom: 4 },
  headerName: { fontSize: 28, fontWeight: 900, color: ink, margin: '0 0 6px', fontFamily: font, letterSpacing: 0.3 },
  headerSub: { fontSize: 13, color: muted, fontFamily: 'Georgia, serif', margin: 0 },
  groupTag: { fontSize: 11, color: '#5C3A1E', fontFamily: font, letterSpacing: 1.5, margin: '5px 0 0', display: 'inline-flex', alignItems: 'center', gap: 5, textTransform: 'uppercase' },
  botBadge: { fontSize: 11, color: '#8A7A5A', background: '#EDE8DC', padding: '2px 8px', borderRadius: 2, letterSpacing: 1, fontFamily: font, display: 'inline-block', verticalAlign: 'middle' },
  redLine: { width: 28, height: 3, background: accent, marginBottom: 8 },

  // Cards / sections
  card: { background: '#FFFFFF', border: `1px solid ${cardBorder}`, borderRadius: 4, padding: '24px 28px' },
  section: { display: 'flex', flexDirection: 'column', gap: 12 },
  sectionHead: { display: 'flex', flexDirection: 'column' },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: ink, margin: 0, fontFamily: font, letterSpacing: 0.4 },

  // Stats grid (4-col → 2-col on mobile via className)
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 },

  // Stat card
  statCard: {
    background: '#FFFFFF', border: `1px solid ${cardBorder}`, borderRadius: 4,
    position: 'relative',
  },
  statCardTop: { padding: '14px 14px 12px', position: 'relative' },
  statCardTitleRow: { display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 },
  statCardTitle: { fontSize: 9, color: muted, fontFamily: font, letterSpacing: 2, textTransform: 'uppercase' },
  statCardSub: { fontSize: 11, color: muted, fontFamily: 'Georgia, serif', marginTop: 6, lineHeight: 1.5 },

  // Tooltip
  tooltipWrap: { position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 },
  tooltipIcon: {
    width: 14, height: 14, borderRadius: '50%',
    border: `1px solid ${cardBorder}`, display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 8, color: muted, fontFamily: 'Georgia, serif',
    lineHeight: 1, userSelect: 'none',
  },
  tooltipBox: {
    position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)',
    background: '#1A0A00', color: '#F5F0E8',
    fontSize: 11, fontFamily: 'Georgia, serif',
    padding: '6px 10px', borderRadius: 3,
    whiteSpace: 'normal', width: 200, lineHeight: 1.5,
    zIndex: 300, pointerEvents: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },

  // Basho history table
  historyWrap: {},
  historyTableCard: { background: '#FFFFFF', border: `1px solid ${cardBorder}`, borderRadius: 4, overflow: 'hidden' },
  historyHeaderRow: { display: 'flex', alignItems: 'center', padding: '8px 16px', gap: 4, background: '#FDFCF9' },
  historyCell: { fontSize: 13, fontFamily: font, color: ink, padding: '2px 0' },
  historyNum: { width: 44, textAlign: 'right', flexShrink: 0 },
  historyHeaderDivider: { borderBottom: `2px solid ${cardBorder}` },
  historyDataRow: {
    display: 'flex', alignItems: 'center',
    padding: '9px 16px', gap: 4,
    borderBottom: `1px solid ${bg}`,
  },

  // Account / form
  field: { marginBottom: 14 },
  label: { display: 'block', fontSize: 10, color: muted, fontFamily: font, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 5 },
  sectionLabel: { fontSize: 10, color: muted, fontFamily: font, letterSpacing: 2, textTransform: 'uppercase', margin: '0 0 12px' },
  input: { width: '100%', padding: '8px 10px', fontFamily: font, fontSize: 14, color: ink, background: '#FDFCF9', border: `1px solid ${cardBorder}`, borderRadius: 2, outline: 'none', boxSizing: 'border-box' },
  divider: { borderTop: `1px solid ${cardBorder}`, margin: '18px 0 14px' },
  msg: (type) => ({ fontSize: 12, fontFamily: font, margin: '0 0 12px', color: type === 'success' ? '#1A6A1A' : accent }),
  saveBtn: { padding: '10px 24px', fontFamily: font, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', background: accent, color: '#F5F0E8', border: 'none', borderRadius: 2 },
  deleteBtn: { padding: '10px 24px', fontFamily: font, fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer', background: 'transparent', color: accent, border: `1px solid ${accent}`, borderRadius: 2 },
  emptyMsg: { fontSize: 13, color: muted, fontFamily: font, fontStyle: 'italic', margin: 0 },

  // Email prefs toggle
  toggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 },
  toggleInfo: { flex: 1 },
  toggleLabel: { fontSize: 13, color: ink, fontFamily: font, fontWeight: 600, marginBottom: 3 },
  toggleDesc: { fontSize: 11, color: muted, fontFamily: font },
  toggleSwitch: (on) => ({ width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer', background: on ? accent : cardBorder, position: 'relative', flexShrink: 0, padding: 0, transition: 'background 0.2s' }),
  toggleThumb: (on) => ({ position: 'absolute', top: 3, left: on ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#FFFFFF', transition: 'left 0.2s' }),
}
